import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NotebookPen, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, fmtDate } from "@/lib/api";

const lastCharge = (inv) => (inv.updated_at || inv.created_at || "").slice(0, 10);

export default function DraftsPage() {
  const [drafts, setDrafts] = useState(null);

  useEffect(() => {
    api.get("/invoices", { params: { status: "draft" } }).then((r) => setDrafts(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const total = (drafts || []).reduce((s, d) => s + d.total, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Draft Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">Running tabs for bi-weekly or monthly billing. Add a charge each visit, email when you're ready.</p>
        </div>
        {drafts && drafts.length > 0 && (
          <div className="text-left sm:text-right">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Open drafts</div>
            <div className="font-mono font-bold text-xl" data-testid="drafts-open-total">{drafts.length} · {money(total)}</div>
          </div>
        )}
      </div>

      {drafts === null ? (
        <div className="text-sm text-muted-foreground">Loading drafts...</div>
      ) : drafts.length === 0 ? (
        <div data-testid="drafts-empty-state" className="surface p-10 flex flex-col items-start gap-3">
          <div className="h-12 w-12 rounded-full bg-emerald-50 dark:bg-emerald-950/50 grid place-items-center"><NotebookPen className="h-6 w-6 text-emerald-600" /></div>
          <h3 className="text-lg font-semibold">No open drafts</h3>
          <p className="text-sm text-muted-foreground max-w-md">On the Clients tab, tap <b>Send Invoice Now</b> and choose <b>Save Draft</b>. Each later visit adds a dated charge to the same draft until you email it.</p>
          <Link to="/clients" className="text-sm font-semibold text-emerald-600 hover:text-emerald-700">Go to clients →</Link>
        </div>
      ) : (
        <div className="surface divide-y divide-border" data-testid="drafts-list">
          {drafts.map((d) => (
            <Link key={d.id} to={`/drafts/${d.id}`} data-testid={`draft-row-${d.id}`}
              className="flex items-center gap-3 px-4 py-3.5 hover:bg-secondary/50 transition-colors">
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="font-semibold truncate">{d.client?.name}</span>
                  <span className="font-mono text-xs text-muted-foreground shrink-0">{d.invoice_number}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {d.line_items.length} charge{d.line_items.length === 1 ? "" : "s"} · started {fmtDate(d.date)} · last added {fmtDate(lastCharge(d))}
                </div>
              </div>
              <div className="font-mono font-bold text-lg shrink-0" data-testid={`draft-total-${d.id}`}>{money(d.total)}</div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
